import { useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import arrow from "../shared/assets/images/down-arrow.svg"
import activeArrow from "../shared/assets/images/active-down-arrow.svg"
import { fadeDown, staggerContainer } from "../shared/lib/motion"
import { useLanguage } from "../shared/context/LanguageContext"
import extraLogo from "../shared/assets/images/extra-logo.svg"
import saIcon from "../shared/assets/icons/sa-flag.svg"
import usIcon from "../shared/assets/icons/use-flag.svg"
import { sendFormDataToSlack } from "../shared/utils/slack"
import { sendFormDataToZoho } from "../shared/utils/zoho"

const ContactUs = () => {
    const { translation, isRTL, lang, setLang } = useLanguage();
    const [isCompanySizeOpen, setIsCompanySizeOpen] = useState(false);
    const [formData, setFormData] = useState({name: '', jobTitle: '', email: '', phoneNumber: '', companyName: '', companySize: '', message: ''});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const companySizeRef = useRef<HTMLDivElement | null>(null)

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const [slackResult, zohoResult] = await Promise.allSettled([
                sendFormDataToSlack(formData, lang, 'form'),
                sendFormDataToZoho(formData, 'form', lang)
            ]);

            const slackSuccess = slackResult.status === 'fulfilled' && slackResult.value.success;
            const zohoSuccess = zohoResult.status === 'fulfilled' && zohoResult.value.success;

            if (slackSuccess || zohoSuccess) {
                setSubmitStatus('success');
                setFormData({name: '', jobTitle: '', email: '', phoneNumber: '', companyName: '', companySize: '', message: ''});

                setTimeout(() => {
                    setSubmitStatus('idle');
                }, 3000);
            } else {
                setSubmitStatus('error');
                console.error('فشل إرسال البيانات:', {
                    slack: slackResult.status === 'rejected' ? slackResult.reason : slackResult.value.error,
                    zoho: zohoResult.status === 'rejected' ? zohoResult.reason : zohoResult.value.error
                });
            }
        } catch (error) {
            setSubmitStatus('error');
            console.error('حدث خطأ:', error);
        } finally {
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        const close = (event: MouseEvent) => {
            if (!companySizeRef.current) return
            const target = event.target as Node
            if (!companySizeRef.current.contains(target)) {
                setIsCompanySizeOpen(false)
            }
        }
        document.addEventListener("mousedown", close)
        return () => document.removeEventListener("mousedown", close)
    }, [])

  return (
    <section className="flex flex-col items-center justify-center gap-6 md:gap-8 w-full py-10 sm:py-12 lg:py-16 bg-secondary">
        <div className="flex justify-between w-full max-w-[18rem] md:max-w-[24rem]">
            {/* Logo */}
            <motion.div className="flex items-center justify-start" variants={fadeDown}>
                <div className="flex-col flex">
                    <img src={lang === "ar" ? "/logos/logo-ar.svg" : "/logos/logo.svg"} alt="NowPay" className="h-6 md:h-10 w-auto" />
                    <div className="flex gap-5 justify-end">
                        <img src={extraLogo} alt="eXtra" className="w-8 h-auto"/>
                    </div>
                </div>
            </motion.div>
            {/* Language Switcher */}
            <motion.div variants={fadeDown} className="flex items-start justify-end">
                <button
                onClick={() => setLang(lang === "en" ? "ar" : "en")}
                className="hover-scale border border-primary rounded-full p-2"
                >
                <div className="flex items-center justify-center gap-1 md:gap-2">
                    <span className="md:text-lg text-sm">
                    {lang === "en" ?
                        <img src={saIcon} alt="AR" className="w-4 h-3" /> : <img src={usIcon} alt="ENG" className="w-4 h-3" />}
                    </span>
                    <span className={`block text-base font-normal text-primary ${lang === "en" ? "font-arabic" : ""}`}>
                    {lang === "en" ? translation.arabic : translation.english}
                    </span>             
                </div>
                </button>
            </motion.div>
        </div>
        <div className="mx-auto w-full max-w-5xl px-5">
            <motion.form 
                onSubmit={onSubmit}
                dir={isRTL ? "rtl" : "ltr"}
                className="mx-auto w-full max-w-[29.75rem] bg-white rounded-[1.875rem] p-8 sm:p-10 lg:p-12 flex flex-col gap-4 sm:gap-[1.375rem] lg:gap-8 shadow-[0_12px_40px_rgba(16,24,40,0.12)]"
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.98 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
            >
            <motion.div
                className="flex flex-col gap-4 sm:gap-[1.375rem] lg:gap-8"
                variants={staggerContainer(0.08, 0.04)}
                initial="hidden"
                animate="show"
            >
                <motion.div className="flex flex-col gap-[0.375rem] lg:gap-3 text-primary" variants={fadeDown}>
                    <h2 className="text-2xl sm:text-[1.75rem] lg:text-[2rem] leading-[100%] font-bold">{translation.contactUs}</h2>
                    <p className="text-sm sm:text-base lg:text-lg leading-[1.25]">{translation.contactDetails}</p>
                </motion.div>

                {submitStatus === 'success' && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm"
                    >
                        ✅ {translation.formSubmitSuccess}
                    </motion.div>
                )}
                {submitStatus === 'error' && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm"
                    >
                        ❌ {translation.formSubmitError}
                    </motion.div>
                )}
                <motion.div className="flex flex-col gap-3 sm:gap-[1.125rem] md:gap-6 lg:gap-8 pe-2 -me-2" variants={fadeDown}>
                    <div className="flex flex-col gap-1 text-primary">
                        {formData.name && <label className="text-sm leading-[1.18] font-bold">{translation.formName} <span className="text-red-500">*</span></label>}
                        <input
                            type="text"
                            placeholder={translation.formName + " *"}
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            required
                            disabled={isSubmitting}
                            dir={isRTL ? "rtl" : "ltr"}
                            className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg disabled:opacity-50"
                        />
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        {formData.jobTitle && <label className="text-sm leading-[1.18] font-bold">{translation.formJobTitle}</label>}
                        <input
                            type="text"
                            placeholder={translation.formJobTitle}
                            value={formData.jobTitle}
                            onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                            disabled={isSubmitting}
                            dir={isRTL ? "rtl" : "ltr"}
                            className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg disabled:opacity-50"
                        />
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        {formData.email && <label className="text-sm leading-[1.18] font-bold">{translation.formEmail} <span className="text-red-500">*</span></label>}
                        <input
                            type="email"
                            placeholder={translation.formEmail + " *"}
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                            disabled={isSubmitting}
                            dir={isRTL ? "rtl" : "ltr"}
                            className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg disabled:opacity-50"
                        />
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        {formData.phoneNumber && <label className="text-sm leading-[1.18] font-bold">{translation.formPhoneNumber} <span className="text-red-500">*</span></label>}
                        <input
                            type="tel"
                            placeholder={translation.formPhoneNumber + " *"} 
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                            required
                            pattern="[0-9\+\-\s\(\)]{10,}"
                            title="Please enter a valid phone number"
                            dir={isRTL ? "rtl" : "ltr"}
                            disabled={isSubmitting}
                            className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg disabled:opacity-50"
                        />
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        {formData.companyName && <label className="text-sm leading-[1.18] font-bold">{translation.formCompanyName}</label>}
                        <input
                            type="text"
                            placeholder={translation.formCompanyName}
                            value={formData.companyName}
                            onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                            disabled={isSubmitting}
                            dir={isRTL ? "rtl" : "ltr"}
                            className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg disabled:opacity-50"
                        />
                    </div>
                    <div className="flex flex-col gap-1 relative" ref={companySizeRef}>
                        {formData.companySize && (<label className="text-sm leading-[1.18] font-bold text-primary">{translation.companySize}</label>)}
                        <span className={`${lang === "ar" ? "" : "hidden"} pointer-events-none absolute left-2 bottom-1 -translate-y-1/2`}>
                            <img src={isCompanySizeOpen ? activeArrow : arrow} alt="arrow" />
                        </span>
                        <button
                            type="button"
                            onClick={() => setIsCompanySizeOpen((prev) => !prev)}
                            disabled={isSubmitting}
                            className={
                                `text-start w-full border-b border-[#DCDFE1] py-2 bg-transparent focus:outline-none text-base lg:text-lg disabled:opacity-50
                                ${formData.companySize ? "text-primary" : "text-[#8E9CA7]"}`
                            }
                        >
                            {formData.companySize || translation.companySize}
                        </button>
                        <span className={`${lang === "en" ? "" : "hidden"} pointer-events-none absolute right-2 bottom-1 -translate-y-1/2`}>
                            <img src={isCompanySizeOpen ? activeArrow : arrow} alt="arrow" />
                        </span>
                        {isCompanySizeOpen && (
                            <div className="absolute bottom-full left-0 mb-2 z-[9999] w-full max-h-[40vh] overflow-y-auto rounded-xl bg-white shadow-2xl border border-gray-200">
                                {["1-50", "51-200", "201-500", "501-1000", "1000+"].map((size) => (
                                    <button
                                        key={size}
                                        type="button"
                                        onClick={() => {
                                            setFormData({ ...formData, companySize: size })
                                            setIsCompanySizeOpen(false)
                                        }}
                                        className={`flex items-center w-full text-left px-4 py-3 text-sm sm:text-base hover:bg-gray-100 transition ${size === formData.companySize && "bg-gray-100 font-semibold"}`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        {formData.message && <label className="text-sm leading-[1.18] font-bold">{translation.formMessage}</label>}
                        <input 
                            type="text"
                            placeholder={translation.formMessage}
                            value={formData.message}
                            onChange={(e) => setFormData({...formData, message: e.target.value})}
                            disabled={isSubmitting}
                            dir={isRTL ? "rtl" : "ltr"}
                            className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg disabled:opacity-50"
                        />
                    </div>
                </motion.div>
                <motion.button 
                    className="w-full pt-2 lg:pt-3" 
                    type="submit" 
                    variants={fadeDown}
                    disabled={isSubmitting}
                >
                    <div className={`h-11 lg:h-[3.625rem] rounded-[6.25rem] py-3 lg:py-4 px-[3.75rem] bg-softGreen hover:bg-[#6BB37F] text-base sm:text-lg lg:text-[1.375rem] font-semibold leading-[100%] text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed`}>
                        {isSubmitting 
                            ? translation.formSubmitting 
                            : translation.send
                        }
                    </div>
                </motion.button>
            </motion.div>
            </motion.form>
        </div>
    </section>
  )
}

export default ContactUs
